import React, { useContext, useEffect, useState, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import Price from '../../../models/Price'
import Option from '../../../models/Option'
import { CustomerContext } from '../../../contexts/CustomerContext'
import placeholder from '../../../assets/placeholder_elementary.png'
import OptionDisplay from '../../OptionDisplay'
import PackageDisplay from '../../PackageDisplay/PackageDisplay'
import { ToastContainer } from 'react-toastify'
import { OrderPackage, Package } from '../../../models';
import styles from './OptionSelection.module.sass'
import Spinner from "../../Spinner";
import NavBar from "../../PackageDisplay/NavBar"
import Typography from '../../Typography'
import Pose from "../../../models/Pose";
import useQueryString from "../../../hooks/use-query-string";
import classes from "../Sittings/Sittings.module.sass";
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import routes from '../../../routes';
import usePoses from '../../../hooks/usePoses';
import useOfferCoupon from '../../../hooks/useOfferCoupon';
import useApplyOfferToCart from '../../../hooks/useApplyOfferToCart';
import PromoBanner from '../../PromoBanner';

const useOptions = (packageId, schoolId) => {
  const [options, setOptions] = useState([])

  useEffect(() => {
    const priceScope = Price.where({ for_school: schoolId, hasPrice: true });

    Option
      .includes(['image_types', 'prices'])
      .merge({ prices: priceScope })
      .where({ packageId })
      .order('abbreviation')
      .all()
      .then((response) => setOptions(response.data))
  }, [packageId])

  return options
}

const usePose = (favoritesIds) => {
  const [pose, setPose] = useState()
  const [loading, setLoading] = useState(true)
  const loadedRef = useRef(null)

  useEffect(() => {
    console.log('usePose effect running', { favoritesIds });
    
    if (!favoritesIds || (Array.isArray(favoritesIds) && favoritesIds.length === 0)) {
      console.log('No favoritesIds, setting loading false');
      setLoading(false)
      return;
    }
    
    const poseId = [favoritesIds].flat()[0];
    
    // Don't fetch if we've already loaded this pose
    if (loadedRef.current === poseId) {
      console.log('Already loaded this pose, skipping fetch');
      return;
    }
    
    console.log('Fetching pose with id:', poseId);
    loadedRef.current = poseId;
    setLoading(true)
    
    Pose
      .find(poseId)
      .then((response) => {
        console.log('Pose fetch success:', response.data);
        console.log('About to setPose and setLoading(false)');
        setPose(response.data);
        setLoading(false);
        console.log('Called setPose and setLoading(false)');
      })
      .catch((error) => {
        console.error('Error fetching pose:', error)
        setPose(null)
        setLoading(false)
      })
  }, [Array.isArray(favoritesIds) ? favoritesIds.join(',') : favoritesIds]) 

  console.log('usePose returning:', { pose, loading, favoritesIds });
  return { pose, loading }
}

const OptionSelectionContent = ({ background, foreground,
                                  multiPoseHandler, orderPackage = new OrderPackage(), packageId, accessCodeOnly,
                                  isElementary = true,
                                  heading = null,
                                  hideYearbookOption = false,
                                  onSkip = null,
                                  preorderPose }) => {
  const { customer } = useContext(CustomerContext);
  const [currentPackage, setCurrentPackage] = useState()
  const [selectedIcon, setSelectedIcon] = useState('packages')
  const { currentOrderPackage, currentSchool } = customer
  const allOptions = useOptions(packageId, currentSchool.id)
  // Senior upsell step (hideYearbookOption) drops the standalone $0 "yearbook" Option
  // card — the yearbook pose is now captured in its own step. Default off → the
  // elementary/undergrad flow keeps every option. Filtering here flows through to
  // both the collection memo and the old-display displayOptions below.
  const options = useMemo(() => (
    hideYearbookOption
      ? allOptions.filter((opt) => !opt.name?.toLowerCase().includes('yearbook'))
      : allOptions
  ), [allOptions, hideYearbookOption])
  const [collection, setCollection] = useState([])
  const { favorites: favoritesIds } = useQueryString()
  const { pose: selectedPose, loading: poseLoading } = usePose(favoritesIds)

  console.log('Component render:', { 
    favoritesIds, 
    selectedPose, 
    poseLoading,
    hasCurrentPackage: !!currentPackage,
    selectedIcon
  });
  
  const { data: poses } = usePoses(customer?.currentStudent?.id, packageId);
  const history = useHistory();

  // Promotional offer for THIS pricelist — the same time-windowed for_landing_page engine
  // the senior flow uses, so elementary/undergrad promos work identically. studentId here
  // is the external school-scoped id (Student#studentId), which the resolver looks up.
  const { data: offer } = useOfferCoupon(customer?.currentStudent?.studentId, currentSchool.id, packageId);
  // Auto-apply ONLY where the promo UI actually renders — the new-package-builder path
  // (see the `currentPackage.useNewPackageBuilder` return below). On a legacy-display
  // (OptionDisplay) pricelist the banner + per-card discount don't show, so applying the
  // coupon there would be a silent, unexplained discount at checkout.
  useApplyOfferToCart(currentPackage?.useNewPackageBuilder ? offer : null);

  // Check if all poses are JPGs
  const allPosesAreJpg = poses && poses.length > 0 && poses.every(pose => 
    pose.imageUrl && pose.imageUrl.toLowerCase().includes('.jpg')
  );

  console.log('All poses are JPG:', allPosesAreJpg);

  useEffect(() => {
    Package
      .find(packageId)
      .then((res) => {
        setCurrentPackage(res.data)
      })
  }, [packageId])

  useEffect(() => {
    if (options.length === 0) return;

    let collection

    // NavBar's only tab now sets 'packages' (the old 'featured' tab was removed), so that is
    // the only real branch; the else just keeps `collection` defined defensively.
    if (selectedIcon === 'packages') {
      // ALL packages (non-add-on), featured-first then most expensive. Featured-only would
      // hide non-featured best-sellers (e.g. "Digital Value Pack"), so the single Packages
      // tab shows everything with the curated/featured ones leading. Priceless/empty options
      // fall out downstream (PackageDisplay renders nothing without a price).
      collection = options.filter((opt) => !opt.code).sort((a, b) => {
        if (!!a.featured !== !!b.featured) return a.featured ? -1 : 1
        return (b.prices?.[0]?.price || 0) - (a.prices?.[0]?.price || 0)
      })
    } else {
      collection = []
    }

    // Filter collection if all poses are JPGs
    if (allPosesAreJpg) {
      collection = collection.filter(option => 
        option.name && (
          option.name.includes('8x10') || 
          option.name.includes('Team Picture')
        )
      );
      console.log('Filtered collection for JPG-only:', collection);
    }

    setCollection(collection)
  }, [options, selectedIcon, allPosesAreJpg])

  let bgUrl = ''
  if (currentOrderPackage?.background?.imageUrl) {
    bgUrl = currentOrderPackage.background.imageUrl
  } else if (background?.imageUrl) {
    bgUrl = background.imageUrl
  } else if (typeof background === 'string') {
    bgUrl = background
  }

  console.log('Final bgUrl:', bgUrl);

  let studentImageUrl
  if (foreground) {
    studentImageUrl = foreground
  } else if (currentOrderPackage && currentOrderPackage.studentImage) {
    studentImageUrl = currentOrderPackage.studentImage.watermarkUrl
  } else if (currentPackage && !currentPackage.placeholder.includes('placeholder.png')) {
    studentImageUrl = currentPackage.placeholder
  } else {
    studentImageUrl = placeholder
  }

  if (!currentPackage) {
    return (
      <div className={styles.spinnerContainer}>
        <Spinner color="blue" />
      </div>
    )
  }

  if (favoritesIds && favoritesIds.length > 0 && poseLoading) {
    console.log('Showing spinner because poseLoading is true');
    return (
      <div className={classes.spinnerContainer}>
        <Spinner color="blue" />
      </div>
    )
  }

  // "Prints / Digital Files" is no longer an in-page builder tab: the NavBar button routes
  // to the Photo Gifts & Add Ons page (routes.holiday.builder). pid = the current package;
  // no order-package is required (the add-ons page creates one when something is added).
  const goToAddons = () => history.push(routes.holiday.builder({ optional: true, pid: packageId }));

  // Optional "No thanks" affordance — only rendered when an onSkip callback is
  // passed (senior upsell step). Lets a senior proceed with just their $0 yearbook
  // line. Default null → elementary/undergrad render nothing here.
  const renderSkip = () => {
    if (!onSkip) return null;

    return (
      <div style={{ textAlign: 'center', margin: '1.5em 0 3em' }}>
        <button
          type="button"
          onClick={onSkip}
          style={{
            background: 'none', border: 'none', color: '#3883c0',
            cursor: 'pointer', fontSize: '1.3em', fontWeight: 'bold',
          }}
        >
          No thanks, just my yearbook photo now
        </button>
      </div>
    );
  };

  if (currentPackage && currentPackage.useNewPackageBuilder) {
    return (
      <>
        <ToastContainer position="bottom-left" />

        <NavBar selectedIcon={selectedIcon} setSelectedIcon={setSelectedIcon} onPrints={goToAddons} />

        <div className={styles.packageContainer}>
          {heading && (
            <div className={styles.heading}>
              <Typography align="center" weight="bold">{heading}</Typography>
            </div>
          )}
          <PromoBanner offer={offer} className="mt-2" />
          {collection.map((option) => (
            <PackageDisplay
              orderPackage={orderPackage}
              backgroundUrl={bgUrl}
              studentImageUrl={studentImageUrl}
              key={option.id}
              multiPoseHandler={multiPoseHandler ? (id) => multiPoseHandler(options.find((opt) => opt.id === id)) : null}
              option={option}
              price={option.prices[0]}
              imageTypes={option.imageTypes}
              currentOrderPackage={currentOrderPackage}
              packageId={packageId}
              accessCodeOnly={accessCodeOnly}
              selectedPose={selectedPose}
              preorderPose={preorderPose}
              isElementary={isElementary}
              offer={offer}
            />
          ))}
        </div>
        {renderSkip()}
      </>
    )
  }

  // Filter options for the old display if all poses are JPGs
  const displayOptions = allPosesAreJpg 
    ? options.filter(option => 
        option.name && (
          option.name.includes('8x10') || 
          option.name.includes('Team Picture')
        )
      )
    : options;

  console.log('Display options:', displayOptions);

  return (
    <>
      <ToastContainer position="bottom-left" />
      {displayOptions.map((option) => (
        <OptionDisplay
          backgroundUrl={bgUrl}
          studentImageUrl={studentImageUrl}
          key={option.id}
          multiPoseHandler={multiPoseHandler ? (id) => multiPoseHandler(options.find((opt) => opt.id === id)) : null}
          option={option}
          price={option.prices[0]}
          imageTypes={option.imageTypes}
          currentOrderPackage={currentOrderPackage}
          packageId={packageId}
          accessCodeOnly={accessCodeOnly}
        />
      ))}
      {renderSkip()}
    </>
  )
}

OptionSelectionContent.propTypes = {
  background: PropTypes.string,
  foreground: PropTypes.string,
  multiPoseHandler: PropTypes.func,
  packageId: PropTypes.string.isRequired,
  hideYearbookOption: PropTypes.bool,
  onSkip: PropTypes.func,
}

OptionSelectionContent.defaultProps = {
  background: null,
  foreground: null,
  multiPoseHandler: null,
  hideYearbookOption: false,
  onSkip: null,
}

export default OptionSelectionContent
